import { useCallback, useMemo, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from 'react-oidc-context';

import { bamClientResourceName, Jwt } from '@/types/jwt';
import { loginPostedLocalStorageKey } from '@/utils/auth/openidConnectConfig';

import type { AuthApi, AuthUser } from './types';

export default function useOidcAdapter(): AuthApi {
  const auth = useAuth();
  const pendingSilentRenewRef = useRef<Promise<string | undefined> | null>(null);

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    // return token if not expired
    if (auth.user?.access_token && !auth.user.expired) {
      return auth.user.access_token;
    }
    // if expired (or missing), probeer silent renew (gedeeld tussen gelijktijdige aanroepers)
    pendingSilentRenewRef.current ??= auth
      .signinSilent()
      .then((renewedUser) => renewedUser?.access_token)
      .finally(() => {
        pendingSilentRenewRef.current = null;
      });
    return pendingSilentRenewRef.current;
  }, [auth]);

  // Probeer nog eens in te loggen wanneer het token verlopen is op de achtergrond
  // en een eerdere silent refresh is mislukt,
  // zie ook issue https://github.com/authts/oidc-client-ts/issues/251
  const onTokenExpired = useCallback<AuthApi['onTokenExpired']>(
    (cb) => {
      auth.events.addAccessTokenExpired(cb);
      return () => auth.events.removeAccessTokenExpired(cb);
    },
    [auth],
  );

  const onSilentRenewError = useCallback<AuthApi['onSilentRenewError']>(
    (cb: (err: Error) => void) => {
      const handler = (e: unknown) => cb(e instanceof Error ? e : new Error(String(e)));
      auth.events.addSilentRenewError(handler);
      return () => auth.events.removeSilentRenewError(handler);
    },
    [auth],
  );

  const user: AuthUser | null = useMemo(() => {
    if (auth.user) {
      return {
        username:
          auth.user.profile.preferred_username === '' ? auth.user.profile.preferred_username : auth.user.profile.email,
        firstName: auth.user.profile.given_name,
        allRolesForUser: (jwtDecode(auth.user.access_token) as Jwt).resource_access?.[bamClientResourceName]?.roles, // NOSONAR (probleem met casting naar JWT en kan niet in sonar ignore)
      };
    }
    return null;
  }, [auth]);

  return useMemo<AuthApi>(
    () => ({
      provider: 'oidc',
      user,
      isAuthenticated: Boolean(user),
      isLoading: auth?.isLoading,
      loginError: false,
      getAccessToken,
      login: async () => {
        await auth.signinRedirect({
          extraQueryParams: {
            kc_idp_hint: 'NSWPOL-ADFS',
          },
        });
      },
      logout: async () => {
        sessionStorage.setItem(loginPostedLocalStorageKey, 'false');
        auth.removeUser().finally(() =>
          auth.signoutRedirect({
            extraQueryParams: {
              id_token_hint: `${auth.user?.id_token ?? ''}`,
              post_logout_redirect_uri: window.location.href,
            },
          }),
        );
      },
      onTokenExpired,
      onSilentRenewError,
    }),
    [user, getAccessToken, onTokenExpired, onSilentRenewError],
  );
}
